import { StateVariant } from "@tagged-state/core";
import { type FaceTecSDKInstance } from "facetec-sdk";
import { useState } from "react";
import useRcEffect from "../rcEffect/useRcEffect";
import { AcquireMode } from "../singleton/createSingleton";
import { FaceTecInitParams, initialize } from "./init";
import faceTecSingleton from "./singleton";

export type State =
  | StateVariant<"uninit">
  | StateVariant<"pending">
  | StateVariant<"ready", { instance: FaceTecSDKInstance }>
  | StateVariant<"error", { error: Error }>
  | StateVariant<"evicted">;

// The init errors are reflected in the state, and so is the lease being
// evicted by another acquisition - before or after the instance is ready, as
// an evicted instance is no longer usable.
const useManageFaceTecInstance = (
  initParams: FaceTecInitParams,
  mode: AcquireMode,
): State => {
  const [state, setState] = useState<State>({ tag: "uninit", data: {} });

  useRcEffect(() => {
    // Ignore the results of the init once it is superseded.
    let active = true;
    // Set once the init has failed or the lease is evicted.
    let ended = false;

    const set = (newState: State) => {
      if (!active) return;
      console.log("[facetec instance] state", newState.tag, newState.data);
      setState(newState);
    };

    set({ tag: "pending", data: {} });

    const { item, evicted, release } = faceTecSingleton.acquire(
      () => initialize(initParams),
      mode,
    );

    item.then(
      (instance) => {
        if (ended) return;
        set({ tag: "ready", data: { instance } });
      },
      (error: Error) => {
        if (ended) return;
        ended = true;
        set({ tag: "error", data: { error } });
      },
    );
    evicted.then(() => {
      if (ended) return;
      ended = true;
      set({ tag: "evicted", data: {} });
    });

    return () => {
      active = false;
      release();
    };
  }, [initParams, mode]);

  return state;
};

export default useManageFaceTecInstance;
